import React, { useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { getAngularBaseUrl } from 'shared/services/legacy';
import Form, { Field, Submit } from '../../containers/form';
import { StyledCardActions } from './styled';

import style from './style.module.scss';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import { useApiRequest } from '../hooks/useApiRequest';
import { resetPassword } from './verifyUserApi';
import LoginBaseContainer from './LoginBaseContainer';
import BoxedMessage from './BoxedMessage';
import { useQueryString } from '../hooks/useQueryString';
import { lookupCorrectEmail } from './emailLookup';
import { getPasswordResetEmail, sendSlackMessage } from './utils';

const successText =
  'Please check your inbox and spam folder. We will send an email with a reset password link if we find an account attached to the ' +
    'provided email address.';

const PasswordReset = () => {
  const { push } = useHistory();
  const query = useQueryString();
  const [rest, makeResetRequest] = useApiRequest<unknown>();
  const [email] = useState(
    decodeURIComponent(
      (query.get('email') || query.get('username') || '').replace(/ /g, '+'),
    ),
  );

  const handleSubmit = useCallback(
    async ({ username }: { username: string }) => {
      const correctEmail = await lookupCorrectEmail(username);
      sendSlackMessage(getPasswordResetEmail(correctEmail));
      makeResetRequest(resetPassword(correctEmail));
    },
    [push, makeResetRequest],
  );

  return (
    <LoginBaseContainer>
      {rest.type === 'REQUEST_SUCCESS' ? (
        <BoxedMessage message={successText} />
      ) : (
        <Form
          initialValues={{ username: email }}
          onSubmit={handleSubmit}
          onDirty={undefined}
          onChange={undefined}
          onValidate={undefined}
          onError={undefined}
        >
          <div className={style.label__wrapper}>
            <h1>Reset Password</h1>
          </div>
          <div>
            <label htmlFor="email" className="form__label">
              Email
            </label>
            <Field
              name="username"
              label="Email"
              id="email"
              type="email"
              disabled={Boolean(email)}
              fullWidth
              required
              onChange={undefined}
              useFormControl={undefined}
              maxWidth={undefined}
              inputRef={undefined}
              tooltip={undefined}
            />
            <br />
            <br />
            <TrueImpactText>
              <b>Note:</b> after 3 password reset attempts, your account will be
              locked for at least one hour.
            </TrueImpactText>
            <TrueImpactText>
              <b>
                Important notice! Please read.
                <br />
              </b>{' '}
              If you haven’t previously logged into this new version of our
              platform, please{' '}
              <a target="_blank" href={`${getAngularBaseUrl()}/#/reset_login`}>
                click here
              </a>{' '}
              to reset your password in our legacy platform. You will then be
              able to log into the new platform with that password. If you have
              any questions, please contact us at{' '}
              <a href="mailto:support@trueimpact.com">support@trueimpact.com</a>
            </TrueImpactText>
            <br />
            <TrueImpactText>
              Back to <Link to="/login">Login</Link>
            </TrueImpactText>
            <br />
          </div>
          <StyledCardActions>
            <Submit
              variant="contained"
              color="primary"
              disabled={rest.type === 'REQUEST_START'}
              alwaysEnabled={false}
            >
              Send Code
            </Submit>
          </StyledCardActions>
        </Form>
      )}
    </LoginBaseContainer>
  );
};

export default PasswordReset;
